'use client';

import Link from 'next/link';
import { useState, useMemo } from 'react';
import { Search, Filter, ArrowUpDown, Clock, Users, BookOpen, X, ChevronRight } from 'lucide-react';
import { getPublishedWorkflows, getWorkflowCategories } from '@/lib/data/workflows';

export default function WorkflowsClient() {
  const allWorkflows = getPublishedWorkflows();
  const categories = getWorkflowCategories();

  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'difficulty' | 'readingTime'>('newest');

  // Filter and sort workflows
  const filteredWorkflows = useMemo(() => {
    let filtered = allWorkflows;

    // Category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(w => w.category === selectedCategory);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(w =>
        w.title.toLowerCase().includes(query) ||
        w.description.toLowerCase().includes(query) ||
        w.audience.some(a => a.toLowerCase().includes(query))
      );
    }

    // Sort
    if (sortBy === 'difficulty') {
      const difficultyOrder = { 'Beginner': 1, 'Intermediate': 2, 'Advanced': 3 };
      filtered = [...filtered].sort((a, b) => difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty]);
    } else if (sortBy === 'readingTime') {
      filtered = [...filtered].sort((a, b) => a.readingTime - b.readingTime);
    }

    return filtered;
  }, [allWorkflows, selectedCategory, searchQuery, sortBy]);

  const categoryColors: Record<string, string> = {
    Security: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
    Network: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    DevOps: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    Development: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    Cloud: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
    Compliance: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
  };

  const categoryBadgeColors: Record<string, string> = {
    Security: 'bg-red-100 text-red-800 border border-red-200',
    Network: 'bg-blue-100 text-blue-800 border border-blue-200',
    DevOps: 'bg-purple-100 text-purple-800 border border-purple-200',
    Development: 'bg-green-100 text-green-800 border border-green-200',
    Cloud: 'bg-cyan-100 text-cyan-800 border border-cyan-200',
    Compliance: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  };

  const difficultyColors: Record<string, string> = {
    Beginner: 'bg-green-50 text-green-700 border border-green-200',
    Intermediate: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    Advanced: 'bg-red-50 text-red-700 border border-red-200',
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section with Background Image */}
      <section className="relative bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white py-20 overflow-hidden">
        {/* Hero Background Image Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20 mix-blend-overlay"
          style={{ backgroundImage: "url('https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/hero-workflows/width=1920,quality=80,format=auto')" }}
        ></div>

        {/* Gradient Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Technical Workflows
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 mb-8 leading-relaxed">
              Step-by-step guides to master IT operations, security, and infrastructure
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-8 py-4 border border-white/20">
                <div className="text-4xl font-bold mb-1">{allWorkflows.length}</div>
                <div className="text-sm text-white/80 font-medium">Workflows</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-8 py-4 border border-white/20">
                <div className="text-4xl font-bold mb-1">{categories.length}</div>
                <div className="text-sm text-white/80 font-medium">Categories</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-8 py-4 border border-white/20">
                <div className="text-4xl font-bold mb-1">
                  {Math.round(allWorkflows.reduce((sum, w) => sum + w.readingTime, 0) / allWorkflows.length)}
                </div>
                <div className="text-sm text-white/80 font-medium">Avg. Minutes</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Filter Bar */}
      <section className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Search and Sort Row */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search workflows by title, description, or audience..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-[#2813e8] transition-all text-sm"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex gap-2">
              <div className="relative">
                <ArrowUpDown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4 pointer-events-none" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'newest' | 'difficulty' | 'readingTime')}
                  className="pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-[#2813e8] appearance-none bg-white cursor-pointer text-sm font-medium"
                >
                  <option value="newest">Newest First</option>
                  <option value="difficulty">By Difficulty</option>
                  <option value="readingTime">By Time</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {(selectedCategory !== 'All' || searchQuery) && (
                <button
                  onClick={() => {
                    setSelectedCategory('All');
                    setSearchQuery('');
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              )}
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                selectedCategory === 'All'
                  ? 'bg-[#2813e8] text-white border-[#2813e8] shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              All <span className="ml-1.5 opacity-75">({allWorkflows.length})</span>
            </button>
            {categories.map(cat => {
              const count = allWorkflows.filter(w => w.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                    selectedCategory === cat
                      ? categoryColors[cat] + ' shadow-sm'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {cat} <span className="ml-1.5 opacity-75">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results Info */}
      {filteredWorkflows.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
          <div className="flex items-center justify-between text-sm">
            <p className="text-gray-600 font-medium">
              Showing <span className="text-gray-900 font-semibold">{filteredWorkflows.length}</span> of <span className="text-gray-900 font-semibold">{allWorkflows.length}</span> workflows
            </p>
            {selectedCategory !== 'All' && (
              <p className="text-gray-500">
                Filtered by: <span className="font-semibold text-gray-900">{selectedCategory}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Workflows Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        {filteredWorkflows.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No workflows found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Try adjusting your search terms or filters to find what you're looking for
            </p>
            <button
              onClick={() => {
                setSelectedCategory('All');
                setSearchQuery('');
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#2813e8] text-white rounded-lg hover:bg-[#1f0fb3] transition-all shadow-sm hover:shadow-md font-medium"
            >
              <X className="w-4 h-4" />
              Clear All Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkflows.map((workflow) => (
              <Link
                key={workflow.slug}
                href={`/blog/${workflow.blogSlug}`}
                className="group bg-white rounded-xl border border-gray-200 hover:border-[#2813e8] hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
              >
                {/* Card Header */}
                <div className="p-6 flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-3xl" role="img" aria-label={workflow.category}>
                      {workflow.icon}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryBadgeColors[workflow.category] || 'bg-gray-100 text-gray-800'}`}>
                      {workflow.category}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#2813e8] transition-colors line-clamp-2 leading-tight">
                    {workflow.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                    {workflow.description}
                  </p>

                  {/* Meta Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${difficultyColors[workflow.difficulty]}`}>
                      <Filter className="w-3 h-3" />
                      {workflow.difficulty}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 border border-gray-200 text-xs font-medium">
                      <Clock className="w-3 h-3" />
                      {workflow.readingTime} min
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 border border-gray-200 text-xs font-medium">
                      <BookOpen className="w-3 h-3" />
                      {workflow.toolCount} tools
                    </span>
                  </div>

                  {/* Audience Tags */}
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {workflow.audience.slice(0, 3).join(', ')}
                      {workflow.audience.length > 3 && ` +${workflow.audience.length - 3} more`}
                    </p>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 group-hover:from-[#2813e8] group-hover:to-[#2cc3d1] transition-all duration-300 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-white transition-colors">
                      View Workflow
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 bg-[size:20px_20px]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Need Help Implementing These Workflows?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Our team of experts can help you implement these workflows and best practices in your organization
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#2813e8] px-8 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl"
            >
              Get Expert Consultation
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              href="/tools"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-3.5 rounded-lg font-semibold hover:bg-white/10 transition-all"
            >
              Browse All Tools
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
